import { useEffect, useRef, useState, type ReactNode } from "react";
import { printSignFile, signFetch, signFileUrl } from "../api/client";
import { fieldLabel, partyColor } from "../lib/palette";
import { previewApply, previewFilled, previewMark } from "../lib/preview";
import { loadPdfFromUrl } from "../pdf/loadPdf";
import type { FieldKind, SignDoc, SignField, SignParty } from "../types";
import { IcoClose, IcoDownload, IcoEye, IcoLink, IcoPrint, IcoSave, IcoSend, IcoShield, IcoTrash } from "../ui/icons";
import { Palette } from "./Palette";
import { PreviewDock } from "./PreviewDock";
import { PreviewDone } from "./PreviewDone";

function BarTip({
  label,
  onClick,
  disabled = false,
  on = false,
  primary = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  on?: boolean;
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`sg-tip${on ? " is-on" : ""}${primary ? " is-send" : ""}`}
      data-tip={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function sizeFor(kind: FieldKind): { w: number; h: number } {
  if (kind === "check" || kind === "radio") return { w: 0.04, h: 0.03 };
  if (kind === "paraphe") return { w: 0.11, h: 0.048 };
  if (kind === "date" || kind === "number") return { w: 0.16, h: 0.042 };
  if (kind === "phone") return { w: 0.2, h: 0.042 };
  if (kind === "select") return { w: 0.22, h: 0.045 };
  if (kind === "stamp") return { w: 0.14, h: 0.1 };
  if (kind === "text" || kind === "name") return { w: 0.22, h: 0.045 };
  return { w: 0.24, h: 0.06 };
}

function makeField(kind: FieldKind, page: number, pos: { x: number; y: number }, sz: { w: number; h: number }, partyId: number): SignField {
  const row: SignField = { kind, page, ...pos, w: sz.w, h: sz.h, label: fieldLabel(kind), party_id: partyId, required: true };
  if (kind === "select") row.options = ["Oui", "Non"];
  return row;
}

function fieldBurned(doc: SignDoc, parties: SignParty[], f: SignField): boolean {
  if (doc.status === "signed") return true;
  if (!doc.has_sealed) return false;
  if (!f.party_id) return true;
  return Boolean(parties.find((p) => p.id === f.party_id)?.signed_at);
}

function clamp(n: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, n));
}

function pageFromPoint(x: number, y: number): { el: HTMLElement; page: number } | null {
  const node = document.elementFromPoint(x, y);
  const el = node instanceof Element ? node.closest<HTMLElement>(".sg-page") : null;
  if (!el) return null;
  const page = Number(el.dataset.page);
  if (!page) return null;
  return { el, page };
}

function placeOn(el: HTMLElement, clientX: number, clientY: number, w: number, h: number, gx = 0.5, gy = 0.5) {
  const box = el.getBoundingClientRect();
  return {
    x: clamp((clientX - box.left) / box.width - gx * w, 0, Math.max(0, 1 - w)),
    y: clamp((clientY - box.top) / box.height - gy * h, 0, Math.max(0, 1 - h)),
  };
}

type PageSize = { w: number; h: number };
type Drag =
  | { type: "draw"; kind: FieldKind; page: number; x0: number; y0: number }
  | { type: "move"; index: number; gx: number; gy: number }
  | { type: "resize"; index: number };

type Draft = { page: number; x: number; y: number; w: number; h: number };

export function Editor({
  doc,
  onClose,
  onSaved,
  onAskSend,
  onTrace,
  onDownload,
  onCopy,
}: {
  doc: SignDoc;
  onClose: () => void;
  onSaved: (next: SignDoc) => void;
  onAskSend: (next: SignDoc) => void;
  onTrace: () => void;
  onDownload: () => void;
  onCopy?: () => void;
}) {
  const [fields, setFields] = useState<SignField[]>(doc.fields ?? []);
  const [parties, setParties] = useState<SignParty[]>(doc.parties ?? []);
  const [partyId, setPartyId] = useState(doc.parties?.[0]?.id ?? 0);
  const [kind, setKind] = useState<FieldKind>("text");
  const [drawing, setDrawing] = useState<FieldKind | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sel, setSel] = useState(-1);
  const [pages, setPages] = useState<PageSize[]>([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hotPage, setHotPage] = useState(0);
  const [preview, setPreview] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [previewVals, setPreviewVals] = useState<Record<number, string>>({});
  const [previewDone, setPreviewDone] = useState(false);
  const [previewMiss, setPreviewMiss] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const fieldsRef = useRef(fields);
  const dragRef = useRef<Drag | null>(null);
  const movedRef = useRef(false);
  const frozen = doc.status !== "draft";

  fieldsRef.current = fields;

  useEffect(() => {
    setFields(doc.fields ?? []);
    setParties(doc.parties ?? []);
    setPartyId(doc.parties?.[0]?.id ?? 0);
    setErr("");
  }, [doc.id]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setPages([]);
    (async () => {
      try {
        const pdf = await loadPdfFromUrl(
          signFileUrl(doc.id, doc.status === "signed" ? "pack" : doc.has_sealed ? "sealed" : "original"),
        );
        const next: PageSize[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: 1.15 });
          next.push({ w: vp.width, h: vp.height });
        }
        if (!live) return;
        setPages(next);
        setLoading(false);
        await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        for (let i = 1; i <= pdf.numPages; i++) {
          if (!live) return;
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: 1.15 });
          const canvas = host.current?.querySelector<HTMLCanvasElement>(`canvas[data-page="${i}"]`);
          const ctx = canvas?.getContext("2d");
          if (!canvas || !ctx) continue;
          canvas.width = vp.width;
          canvas.height = vp.height;
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
        }
        if (live && doc.status === "draft" && doc.page_count !== pdf.numPages) {
          await signFetch("documents.php", {
            method: "PATCH",
            body: JSON.stringify({ id: doc.id, page_count: pdf.numPages }),
          });
        }
      } catch (e) {
        if (live) {
          setLoading(false);
          setErr(e instanceof Error ? e.message : "PDF illisible.");
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [doc.id, doc.page_count]);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const d = dragRef.current;
      if (!d) return;
      movedRef.current = true;
      const hit = pageFromPoint(e.clientX, e.clientY);
      setHotPage(hit?.page ?? 0);
      if (d.type === "draw") {
        if (!hit || hit.page !== d.page) return;
        const box = hit.el.getBoundingClientRect();
        const x1 = clamp((e.clientX - box.left) / box.width, 0, 1);
        const y1 = clamp((e.clientY - box.top) / box.height, 0, 1);
        setDraft({
          page: d.page,
          x: Math.min(d.x0, x1),
          y: Math.min(d.y0, y1),
          w: Math.abs(x1 - d.x0),
          h: Math.abs(y1 - d.y0),
        });
        return;
      }
      if (!hit) return;
      const cur = fieldsRef.current[d.index];
      if (!cur) return;
      if (d.type === "move") {
        const pos = placeOn(hit.el, e.clientX, e.clientY, cur.w, cur.h, d.gx, d.gy);
        setFields((prev) => prev.map((row, i) => (i === d.index ? { ...row, page: hit.page, ...pos } : row)));
        return;
      }
      const box = hit.el.getBoundingClientRect();
      const w = clamp((e.clientX - box.left) / box.width - cur.x, 0.003, Math.max(0.003, 1 - cur.x));
      const h = clamp((e.clientY - box.top) / box.height - cur.y, 0.003, Math.max(0.003, 1 - cur.y));
      setFields((prev) => prev.map((row, i) => (i === d.index ? { ...row, w, h } : row)));
    }

    function onUp(e: PointerEvent) {
      const d = dragRef.current;
      if (!d) return;
      if (d.type === "draw") {
        const hit = pageFromPoint(e.clientX, e.clientY);
        const box = hit?.el.getBoundingClientRect();
        let x = d.x0;
        let y = d.y0;
        let w = 0;
        let h = 0;
        if (hit && hit.page === d.page && box) {
          const x1 = clamp((e.clientX - box.left) / box.width, 0, 1);
          const y1 = clamp((e.clientY - box.top) / box.height, 0, 1);
          x = Math.min(d.x0, x1);
          y = Math.min(d.y0, y1);
          w = Math.abs(x1 - d.x0);
          h = Math.abs(y1 - d.y0);
        }
        const fallback = sizeFor(d.kind);
        if (w < 0.005 && h < 0.005) {
          w = fallback.w;
          h = fallback.h;
          x = clamp(d.x0, 0, 1 - w);
          y = clamp(d.y0, 0, 1 - h);
        } else {
          w = clamp(w, 0.003, Math.max(0.003, 1 - x));
          h = clamp(h, 0.003, Math.max(0.003, 1 - y));
        }
        setFields((prev) => {
          setSel(prev.length);
          return [...prev, makeField(d.kind, d.page, { x, y }, { w, h }, partyId)];
        });
        setDraft(null);
        setDrawing(null);
      }
      dragRef.current = null;
      setHotPage(0);
      if (movedRef.current) {
        window.setTimeout(() => {
          movedRef.current = false;
        }, 80);
      }
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [partyId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && preview) {
        e.preventDefault();
        stopPreview();
        return;
      }
      if (frozen) return;
      if (e.key === "Escape" && drawing) {
        e.preventDefault();
        setDrawing(null);
        setDraft(null);
        dragRef.current = null;
        return;
      }
      if ((e.key === "Backspace" || e.key === "Delete") && sel >= 0 && !drawing && !preview) {
        const t = e.target;
        if (t instanceof HTMLElement) {
          const tag = t.tagName;
          const typing =
            t.isContentEditable ||
            tag === "TEXTAREA" ||
            tag === "SELECT" ||
            (tag === "INPUT" &&
              !["button", "submit", "checkbox", "radio", "file", "reset", "hidden"].includes(
                (t as HTMLInputElement).type,
              ));
          if (typing) return;
        }
        e.preventDefault();
        removeSel();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, frozen, drawing, preview]);

  useEffect(() => {
    if (!preview || previewDone) return;
    const el = host.current?.querySelector<HTMLElement>(`[data-field="${previewStep}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [preview, previewStep, previewDone, loading]);

  function removeSel() {
    if (sel < 0) return;
    setFields((prev) => prev.filter((_, i) => i !== sel));
    setSel(-1);
  }

  function beginDraw(next: FieldKind) {
    if (frozen) return;
    setKind(next);
    setDrawing(next);
    setSel(-1);
    setDraft(null);
  }

  function cancelDraw() {
    setDrawing(null);
    setDraft(null);
    dragRef.current = null;
  }

  function addWithoutDraw() {
    if (!drawing) return;
    const sz = sizeFor(drawing);
    const n = fields.length;
    const x = clamp(0.08 + (n % 4) * 0.05, 0, 1 - sz.w);
    const y = clamp(0.1 + Math.floor(n / 4) * 0.07, 0, 1 - sz.h);
    const page = fields[sel]?.page || 1;
    const next = makeField(drawing, page, { x, y }, sz, partyId);
    setFields((prev) => {
      setSel(prev.length);
      return [...prev, next];
    });
    setDrawing(null);
    setDraft(null);
  }

  function startPreview() {
    if (frozen) return;
    if (fields.length === 0) {
      setErr("Posez au moins un champ pour prévisualiser.");
      return;
    }
    setDrawing(null);
    setDraft(null);
    dragRef.current = null;
    setErr("");
    setPreview(true);
    setPreviewDone(false);
    setPreviewMiss(false);
    setPreviewVals({});
    setPreviewStep(0);
    setSel(0);
  }

  function stopPreview() {
    setPreview(false);
    setPreviewDone(false);
    setPreviewMiss(false);
    setPreviewVals({});
    setPreviewStep(0);
  }

  function goPreview(index: number) {
    const i = clamp(index, 0, Math.max(0, fields.length - 1));
    setPreviewStep(i);
    setSel(i);
    setPreviewMiss(false);
    const row = fields[i];
    if (row?.party_id) setPartyId(row.party_id);
  }

  function commitPreview() {
    const field = fields[previewStep];
    if (!field) return;
    const next = previewApply(field, previewVals[previewStep] ?? "", parties);
    const vals = { ...previewVals, [previewStep]: next };
    setPreviewVals(vals);
    if (field.required && !previewFilled(field.kind, next)) {
      setPreviewMiss(true);
      return;
    }
    setPreviewMiss(false);
    if (previewStep >= fields.length - 1) {
      setPreviewDone(true);
      return;
    }
    goPreview(previewStep + 1);
  }

  function startDraw(page: number, ev: React.PointerEvent<HTMLDivElement>) {
    if (frozen || preview || !drawing) return;
    if ((ev.target as HTMLElement).closest(".sg-field, .sg-field__resize")) return;
    ev.preventDefault();
    const box = ev.currentTarget.getBoundingClientRect();
    const x0 = clamp((ev.clientX - box.left) / box.width, 0, 1);
    const y0 = clamp((ev.clientY - box.top) / box.height, 0, 1);
    movedRef.current = false;
    dragRef.current = { type: "draw", kind: drawing, page, x0, y0 };
    setDraft({ page, x: x0, y: y0, w: 0.01, h: 0.01 });
    setHotPage(page);
  }

  function startMove(index: number, ev: React.PointerEvent<HTMLButtonElement>) {
    if (preview) {
      ev.preventDefault();
      ev.stopPropagation();
      goPreview(index);
      return;
    }
    if (frozen) return;
    ev.preventDefault();
    ev.stopPropagation();
    setDrawing(null);
    setDraft(null);
    setSel(index);
    const f = fields[index];
    if (f.party_id) setPartyId(f.party_id);
    const box = ev.currentTarget.getBoundingClientRect();
    movedRef.current = false;
    dragRef.current = {
      type: "move",
      index,
      gx: clamp((ev.clientX - box.left) / box.width, 0, 1),
      gy: clamp((ev.clientY - box.top) / box.height, 0, 1),
    };
  }

  function startResize(index: number, ev: React.PointerEvent) {
    if (frozen || preview) return;
    ev.preventDefault();
    ev.stopPropagation();
    setDrawing(null);
    setDraft(null);
    setSel(index);
    movedRef.current = false;
    dragRef.current = { type: "resize", index };
  }

  async function save(): Promise<SignDoc | null> {
    setBusy(true);
    setErr("");
    try {
      const res = await signFetch<{ document: SignDoc }>("documents.php", {
        method: "PATCH",
        body: JSON.stringify({ id: doc.id, fields }),
      });
      onSaved(res.document);
      if (res.document.parties) setParties(res.document.parties);
      return res.document;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Enregistrement impossible.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function addParty() {
    const res = await signFetch<{ party: SignParty; parties: SignParty[] }>("parties.php", {
      method: "POST",
      body: JSON.stringify({ document_id: doc.id }),
    });
    setParties(res.parties);
    setPartyId(res.party.id);
  }

  function renameParty(id: number, name: string) {
    setParties((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }

  async function commitParty(id: number, name: string) {
    const n = name.trim();
    if (n.length < 1) return;
    const res = await signFetch<{ parties: SignParty[] }>("parties.php", {
      method: "PATCH",
      body: JSON.stringify({ id, name: n }),
    });
    setParties(res.parties);
  }

  async function removeParty(id: number) {
    const res = await signFetch<{ parties: SignParty[] }>("parties.php", {
      method: "DELETE",
      body: JSON.stringify({ id }),
    });
    setParties(res.parties);
    const next = res.parties[0]?.id ?? 0;
    if (partyId === id) setPartyId(next);
    setFields((prev) => prev.map((f) => (f.party_id === id ? { ...f, party_id: next } : f)));
  }

  const activeColor = partyColor(parties, partyId);

  return (
    <div className={`sg-editor${preview ? " is-preview" : ""}`}>
      <div className="sg-editor__bar">
        <strong>{doc.title}</strong>
        {!frozen ? (
          <span className="sg-editor__hint">
            {preview
              ? "Simulation — remplissez comme le signataire."
              : drawing
                ? `Dessinez « ${fieldLabel(drawing)} » sur le PDF, ou ajoutez-le sans tracer.`
                : "Choisissez un élément à droite, puis dessinez-le sur le document."}
          </span>
        ) : (
          <span className="sg-editor__hint" />
        )}
        <div className="sg-editor__acts">
        {sel >= 0 && !frozen && !preview ? (
          <BarTip label="Retirer" onClick={removeSel}>
            <IcoTrash />
          </BarTip>
        ) : null}
        {!frozen ? (
          <BarTip label={busy ? "Enregistrement…" : "Enregistrer"} disabled={busy} onClick={() => void save()}>
            <IcoSave />
          </BarTip>
        ) : null}
        {!frozen ? (
          <BarTip label="Prévisualiser" disabled={busy} on={preview} onClick={() => (preview ? stopPreview() : startPreview())}>
            <IcoEye />
          </BarTip>
        ) : null}
        {!frozen && !preview ? (
          <BarTip
            label="Envoyer"
            primary
            disabled={busy}
            onClick={() =>
              void save()
                .then((next) => {
                  if (next) onAskSend(next);
                })
                .catch((e) => setErr(e instanceof Error ? e.message : "Envoi."))
            }
          >
            <IcoSend />
          </BarTip>
        ) : null}
        {frozen && doc.status !== "signed" && onCopy ? (
          <button type="button" className="sg-linkcopy is-bar" onClick={onCopy}>
            <IcoLink />
            Copier le lien
          </button>
        ) : null}
        <BarTip label="Preuve" onClick={onTrace}>
          <IcoShield />
        </BarTip>
        <BarTip label="Imprimer" onClick={() => printSignFile(doc)}>
          <IcoPrint />
        </BarTip>
        <BarTip label="Télécharger" onClick={onDownload}>
          <IcoDownload />
        </BarTip>
        <BarTip label="Fermer" onClick={onClose}>
          <IcoClose />
        </BarTip>
        </div>
        {err ? <span className="sg-err">{err}</span> : null}
      </div>
      <div className="sg-editor__body">
        <div className={`sg-editor__stage${drawing ? " is-draw" : ""}`} ref={host}>
          {loading ? (
            <>
              <div className="sg-skel" />
              <p className="sg-legal" style={{ color: "#e4c8dc" }}>
                Les pages se dessinent…
              </p>
            </>
          ) : null}
          {pages.map((p, idx) => {
            const pageNo = idx + 1;
            return (
              <div
                key={pageNo}
                className={`sg-page${hotPage === pageNo ? " is-drop" : ""}${drawing ? " is-draw" : ""}`}
                data-page={pageNo}
                style={{ width: p.w, height: p.h, animationDelay: `${idx * 0.06}s` }}
                onPointerDown={(e) => startDraw(pageNo, e)}
              >
                <canvas data-page={pageNo} />
                {fields
                  .map((f, i) => ({ f, i }))
                  .filter(({ f }) => f.page === pageNo && (preview || !fieldBurned(doc, parties, f)))
                  .map(({ f, i }) => (
                    <button
                      key={`${f.kind}-${f.id ?? i}`}
                      type="button"
                      data-field={i}
                      className={`sg-field is-${f.kind}${sel === i ? " is-on" : ""}${preview && previewStep === i && !previewDone ? " is-now" : ""}${preview && previewMark(f.kind, previewVals[i] ?? "") ? " is-filled" : ""}`}
                      style={{
                        left: `${f.x * 100}%`,
                        top: `${f.y * 100}%`,
                        width: `${f.w * 100}%`,
                        height: `${f.h * 100}%`,
                        borderColor: partyColor(parties, f.party_id),
                        background: `${partyColor(parties, f.party_id)}38`,
                        color: "#2a1b36",
                      }}
                      onPointerDown={(e) => startMove(i, e)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (preview) {
                          goPreview(i);
                          return;
                        }
                        setSel(i);
                        if (f.party_id) setPartyId(f.party_id);
                      }}
                    >
                      {preview && f.kind === "signature" && (previewVals[i] ?? "").startsWith("data:image") ? (
                        <img className="sg-field__ink" src={previewVals[i]} alt="" />
                      ) : (
                        <span className={f.kind === "paraphe" && previewVals[i] ? "is-init" : ""}>
                          {preview
                            ? previewMark(f.kind, previewVals[i] ?? "") || (previewStep === i ? "…" : "")
                            : `${f.required ? "* " : ""}${f.label || fieldLabel(f.kind)}`}
                        </span>
                      )}
                      {sel === i && !frozen && !preview ? (
                        <i
                          className="sg-field__resize"
                          onPointerDown={(e) => startResize(i, e)}
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                  ))}
                {draft && draft.page === pageNo ? (
                  <i
                    className="sg-draft"
                    style={{
                      left: `${draft.x * 100}%`,
                      top: `${draft.y * 100}%`,
                      width: `${Math.max(draft.w, 0.01) * 100}%`,
                      height: `${Math.max(draft.h, 0.01) * 100}%`,
                      borderColor: activeColor,
                      background: `${activeColor}33`,
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        {!preview ? (
        <Palette
          kind={kind}
          drawing={drawing}
          partyId={partyId}
          parties={parties}
          frozen={frozen}
          fields={fields}
          sel={sel}
          onKind={beginDraw}
          onCancelDraw={cancelDraw}
          onAddPlain={addWithoutDraw}
          selectedField={sel >= 0 ? fields[sel] : null}
          onSelectParty={(id) => {
            setPartyId(id);
            if (sel >= 0) {
              setFields((prev) => prev.map((row, i) => (i === sel ? { ...row, party_id: id } : row)));
            }
          }}
          onFieldOptions={(options) => {
            if (sel < 0) return;
            setFields((prev) => prev.map((row, i) => (i === sel ? { ...row, options } : row)));
          }}
          onPickField={(index) => {
            setDrawing(null);
            setDraft(null);
            setSel(index);
            const row = fields[index];
            if (row?.party_id) setPartyId(row.party_id);
            if (row) setKind(row.kind);
          }}
          onRenameField={(index, name) => setFields((prev) => prev.map((row, i) => (i === index ? { ...row, label: name } : row)))}
          onRequiredField={(index) => setFields((prev) => prev.map((row, i) => (i === index ? { ...row, required: !row.required } : row)))}
          onDeleteField={(index) => {
            setFields((prev) => prev.filter((_, i) => i !== index));
            setSel((cur) => (cur === index ? -1 : cur > index ? cur - 1 : cur));
          }}
          onMoveField={(from, to) => {
            if (from === to || from < 0 || to < 0) return;
            setFields((prev) => {
              if (from >= prev.length || to >= prev.length) return prev;
              const next = prev.slice();
              const [row] = next.splice(from, 1);
              next.splice(to, 0, row);
              return next;
            });
            setSel(to);
          }}
          onRename={renameParty}
          onRenameCommit={(id, name) => void commitParty(id, name).catch((e) => setErr(e instanceof Error ? e.message : "Nom."))}
          onAdd={() => void addParty().catch((e) => setErr(e instanceof Error ? e.message : "Partie."))}
          onRemove={(id) => void removeParty(id).catch((e) => setErr(e instanceof Error ? e.message : "Partie."))}
        />
        ) : null}
      </div>
      {preview && !previewDone ? (
        <PreviewDock
          fields={fields}
          parties={parties}
          step={previewStep}
          value={previewVals[previewStep] ?? ""}
          miss={previewMiss}
          pageWidth={pages[(fields[previewStep]?.page ?? 1) - 1]?.w || pages[0]?.w || 0}
          onValue={(v) => {
            setPreviewMiss(false);
            setPreviewVals((prev) => ({ ...prev, [previewStep]: v }));
          }}
          onPrev={() => goPreview(previewStep - 1)}
          onNext={commitPreview}
          onClose={stopPreview}
        />
      ) : null}
      {preview && previewDone ? (
        <PreviewDone
          busy={busy}
          onSave={() =>
            void save().then((next) => {
              if (next) stopPreview();
            })
          }
          onClose={stopPreview}
        />
      ) : null}
    </div>
  );
}
